/**
 * NL formatting helpers — single source of truth for date/time/number display.
 */

// ISO YYYY-MM-DD → DD-MM-YYYY
export function formatDateNL(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-');
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

// DD-MM-YYYY → YYYY-MM-DD
export function parseDateNL(nl) {
  if (!nl) return '';
  const [d, m, y] = nl.split('-');
  if (!d || !m || !y) return nl;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function formatDateTimeNL(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mn = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yy} ${hh}:${mn}`;
}

// Decimal hours → "H:MMu"
export function formatHoursHM(decimalHours) {
  if (decimalHours === null || decimalHours === undefined || Number.isNaN(decimalHours)) return '—';
  const sign = decimalHours < 0 ? '-' : '';
  const abs = Math.abs(decimalHours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${sign}${h}:${String(m).padStart(2, '0')}u`;
}

// EUR with NL formatting (comma decimal, dot thousand)
export function formatEUR(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumberNL(value, decimals = 0) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// Get ISO week number for a date
export function getISOWeek(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
}

// ISO week shifted by N weeks (proper date math across year boundaries)
export function getISOWeekOffset(weeks = 0, base = new Date()) {
  const d = new Date(base);
  d.setDate(d.getDate() + weeks * 7);
  return getISOWeek(d);
}

// Number of ISO weeks in a given year (52 or 53). 28 December is always in the
// last ISO week of its year, so its week number = the year's total.
export function isoWeeksInYear(year) {
  return getISOWeek(new Date(Date.UTC(year, 11, 28))).week;
}

// Clamp a (year, week) navigation across year boundaries, honouring 52/53-week years.
export function navigateIsoWeek(year, week, delta) {
  let y = year;
  let w = week + delta;
  while (w < 1) {
    y -= 1;
    w += isoWeeksInYear(y);
  }
  let max = isoWeeksInYear(y);
  while (w > max) {
    w -= max;
    y += 1;
    max = isoWeeksInYear(y);
  }
  return { year: y, week: w };
}

export function isoWeekDates(year, week) {
  // Return [Date] for monday..sunday of given ISO week
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const ISOweekStart = new Date(simple);
  if (dayOfWeek <= 4) {
    ISOweekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
  } else {
    ISOweekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
  }
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(ISOweekStart);
    d.setUTCDate(ISOweekStart.getUTCDate() + i);
    dates.push(d);
  }
  return dates;
}
